/**
 * AdminMobileNav — bottom nav dell'area admin su mobile, con bottom sheet
 * "Altro" per le sezioni secondarie.
 *
 * Estratto da Navbar.tsx il 26 Ott 2026 (punto 7: refactor dei monoliti).
 * Comportamento invariato: stesse rotte, stessi badge, stesso logout.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  Flag,
  Home,
  LayoutDashboard,
  LogOut,
  MoreHorizontal,
  Settings,
  Shield,
  Store,
  Users,
  X,
} from "lucide-react";
import useIsActive from "@/components/nav/useIsActive";

export function AdminOverflowItem({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center gap-1 p-2 rounded-xl text-neutral-400 hover:text-white hover:bg-white/5 active:scale-95 transition-all"
      aria-label={label}
    >
      {icon}
      <span className="text-[10px] font-medium">{label}</span>
    </Link>
  );
}

export default function AdminMobileNav({ onLogout }: { onLogout: () => void }) {
  const isActive = useIsActive();
  const [showOverflow, setShowOverflow] = useState(false);

  function TabItem({
    to,
    icon,
    label,
  }: {
    to: string;
    icon: React.ReactNode;
    label: string;
  }) {
    const active = isActive(to);
    return (
      <Link
        to={to}
        className="relative flex flex-col items-center justify-center gap-1 px-2 py-1 group"
        aria-label={label}
      >
        <div
          className={`transition-all duration-300 ${active ? "text-brand-500 scale-110" : "text-neutral-500 group-hover:text-neutral-300"}`}
        >
          {icon}
        </div>
        <span
          className={`text-[10px] font-medium transition-colors ${active ? "text-brand-500" : "text-neutral-500"}`}
        >
          {label}
        </span>
      </Link>
    );
  }

  return (
    <>
      <div
        className="mobile-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-50"
        style={{
          paddingBottom:
            "env(safe-area-inset-bottom, var(--safe-area-inset-bottom, 0px))",
        }}
      >
        <nav className="bg-black/90 backdrop-blur-3xl border-t border-white/10 px-2 py-2 flex justify-around items-center">
          <TabItem
            to="/admin"
            icon={<LayoutDashboard size={22} />}
            label="Dashboard"
          />
          <TabItem
            to="/admin/users"
            icon={<Users size={22} />}
            label="Utenti"
          />
          <TabItem
            to="/admin/moderation"
            icon={<Shield size={22} />}
            label="Modera"
          />
          <button
            onClick={() => setShowOverflow(true)}
            className="relative flex flex-col items-center justify-center gap-1 px-2 py-1 text-neutral-500 hover:text-neutral-300 group"
            aria-label="Altro"
          >
            <MoreHorizontal size={22} />
            <span className="text-[10px] font-medium">Altro</span>
          </button>
        </nav>
      </div>

      {/* Overflow Bottom Sheet */}
      {showOverflow && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowOverflow(false)}
          />
          <div
            className="absolute bottom-0 left-0 right-0 bg-neutral-950 border-t border-white/10 rounded-t-2xl p-4 pb-8 animate-spring-up"
            style={{
              paddingBottom:
                "calc(1rem + env(safe-area-inset-bottom, var(--safe-area-inset-bottom, 0px)))",
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <div className="flex items-center justify-between mb-4 px-2">
              <h3 className="text-white font-bold text-lg">Pannello Admin</h3>
              <button
                onClick={() => setShowOverflow(false)}
                className="p-1 text-neutral-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <AdminOverflowItem
                to="/admin/dealers"
                icon={<Store size={24} />}
                label="Dealer"
              />
              <AdminOverflowItem
                to="/admin/chapters"
                icon={<Home size={24} />}
                label="Chapter"
              />
              <AdminOverflowItem
                to="/admin/events"
                icon={<Calendar size={24} />}
                label="Eventi"
              />
              <AdminOverflowItem
                to="/admin/analytics"
                icon={<BarChart3 size={24} />}
                label="Analytics"
              />
              <AdminOverflowItem
                to="/admin/ops"
                icon={<AlertTriangle size={24} />}
                label="Ops"
              />
              <AdminOverflowItem
                to="/admin/settings"
                icon={<Settings size={24} />}
                label="Impostazioni"
              />
              <AdminOverflowItem
                to="/admin/security"
                icon={<Shield size={24} />}
                label="Sicurezza"
              />
              <AdminOverflowItem
                to="/admin/feature-flags"
                icon={<Flag size={24} />}
                label="Flags"
              />
              <button
                onClick={() => {
                  setShowOverflow(false);
                  onLogout();
                }}
                className="flex flex-col items-center gap-1 p-2 rounded-xl text-red-400 hover:bg-red-500/10 active:scale-95 transition-all"
                aria-label="Esci"
              >
                <LogOut size={24} />
                <span className="text-[10px] font-medium">Esci</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
